import { CircleUser, FilePlus2, ListChecks, LogIn, LogOut, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useSession, useSessionActions } from "../admin/session.ts";
import { getApiMode } from "../api/config.ts";
import { useLocale } from "../i18n/index.ts";

/**
 * Header entry for the account: "Anmelden" for anonymous visitors, a small menu (account, my
 * suggestions, sign out) once signed in. Only offered when the live API is there.
 */
export function AccountMenu() {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const actions = useSessionActions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (getApiMode() !== "live" || session.status === "loading") return null;

  if (session.status === "anonymous") {
    return (
      <Link to={`/${locale}/login`} className="ds-btn ds-btn--glass ds-glass">
        <LogIn aria-hidden="true" strokeWidth={1.75} />
        {t("diver.signIn")}
      </Link>
    );
  }

  const label = t("diver.menu", { name: session.user?.name ?? "" });
  return (
    <div className="account-menu" ref={root}>
      <button
        type="button"
        className="ds-btn ds-btn--icon ds-btn--glass ds-glass ds-glass--clear"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <CircleUser aria-hidden="true" strokeWidth={1.75} />
      </button>
      {open ? (
        <div
          className="account-menu__panel ds-glass ds-glass--solid"
          role="menu"
          aria-label={label}
        >
          <Link role="menuitem" to={`/${locale}/konto`} onClick={() => setOpen(false)}>
            <UserRound aria-hidden="true" size={18} strokeWidth={1.75} />
            {t("diver.nav_account")}
          </Link>
          {session.staff ? null : (
            <>
              <Link
                role="menuitem"
                to={`/${locale}/konto/vorschlaege`}
                onClick={() => setOpen(false)}
              >
                <ListChecks aria-hidden="true" size={18} strokeWidth={1.75} />
                {t("diver.nav_suggestions")}
              </Link>
              <Link
                role="menuitem"
                to={`/${locale}/konto/vorschlagen/spot`}
                onClick={() => setOpen(false)}
              >
                <FilePlus2 aria-hidden="true" size={18} strokeWidth={1.75} />
                {t("suggest.spotButton")}
              </Link>
            </>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await actions.signOutAndClear();
              void navigate(`/${locale}/`);
            }}
          >
            <LogOut aria-hidden="true" size={18} strokeWidth={1.75} />
            {t("diver.signOut")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
